/**
 * Current-user / tenant lookup. Hits `/v1/me` on the gateway and
 * caches the response in-memory. Mirrors the shape origin/dashboard's
 * `transformCurrentUser` produces:
 *
 *   { userId, identitySub, email, isPlatformAdmin,
 *     currentTenant: { tenantId, tenantName, role },
 *     availableTenants: [...] }
 *
 * In local-dev (`AUTH_REQUIRED === false`) the gateway typically
 * doesn't expose `/v1/me` and there's no signed-in identity to fetch.
 * The hook resolves to a synthetic "Local Dev" tenant so the footer
 * still has a sensible label.
 */
import { useEffect, useSyncExternalStore } from "react";
import { API_BASE_URL, AUTH_REQUIRED } from "./featureFlags.js";
import { authedFetch } from "./restClient.js";
import { createRecoveryRetry } from "./recoveryRetry.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { analyticsRegionOf } from "./analytics/tenantGate.js";

const ME_URL = `${API_BASE_URL.replace(/\/$/, "")}/v1/me`;

const LOCAL_DEV_USER = Object.freeze({
    userId: null,
    identitySub: null,
    email: null,
    isPlatformAdmin: false,
    currentTenant: { tenantId: null, tenantName: "Local Dev", role: null },
    availableTenants: [],
    traceReviewBackendEnabled: true,
});

let _cache = null;
let _inflight = null;
const _listeners = new Set();
const _recovery = createRecoveryRetry(() => { void getCurrentUser(); });

function emitChange() {
    for (const listener of _listeners) listener();
}

function subscribe(listener) {
    _listeners.add(listener);
    const stop = _recovery.subscribe();
    return () => { _listeners.delete(listener); stop(); };
}

function getSnapshot() { return _cache; }

// A mounted dashboard can outlive one auth session. Clear synchronous reads at
// the auth boundary so no caller can observe the previous tenant while the new
// `/v1/me` request is still pending. In-flight requests also carry the epoch
// they started under and are never allowed to repopulate this cache.
onAuthIdentityChange(() => {
    _cache = null;
    _inflight = null;
    _recovery.reset();
    emitChange();
});

// grpc-gateway's default JSON encoder emits camelCase (`tenantName`),
// not the proto's snake_case (`tenant_name`). Origin's auth refresh
// payload had the same gotcha — accept both shapes so this code keeps
// working if the gateway ever flips back to original_name=true.
function pick(obj, snake, camel) {
    if (!obj) return null;
    return obj[camel] ?? obj[snake] ?? null;
}

export function transformCurrentUser(json) {
    if (!json) return null;
    const tenant = json.current_tenant ?? json.currentTenant ?? null;
    const available = json.available_tenants ?? json.availableTenants ?? [];
    return {
        userId: pick(json, "user_id", "userId"),
        identitySub: pick(json, "identity_sub", "identitySub"),
        email:  json.email ?? null,
        isPlatformAdmin: Boolean(json.is_platform_admin ?? json.isPlatformAdmin),
        // Advertised by the server so the dashboard follows it rather than a
        // flag of its own. A gateway built before the field drops it, which
        // reads as false here and hides the Security rail entry.
        traceReviewBackendEnabled: Boolean(
            json.trace_review_backend_enabled ?? json.traceReviewBackendEnabled,
        ),
        // The product analytics region of the current tenant, or null when it
        // has none (lib/analytics/tenantGate.js).
        productAnalyticsRegion: analyticsRegionOf(json),
        currentTenant: tenant
            ? {
                  tenantId:   pick(tenant, "tenant_id",   "tenantId"),
                  tenantName: pick(tenant, "tenant_name", "tenantName"),
                  role:       tenant.role ?? null,
              }
            : null,
        availableTenants: Array.isArray(available)
            ? available.map((t) => ({
                  tenantId:   pick(t, "tenant_id",   "tenantId"),
                  tenantName: pick(t, "tenant_name", "tenantName"),
                  role:       t.role ?? null,
                  isDefault:  Boolean(t.is_default ?? t.isDefault),
              }))
            : [],
    };
}

async function fetchMe() {
    if (!AUTH_REQUIRED) return LOCAL_DEV_USER;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException("User lookup timed out", "TimeoutError")), 15_000);
    try {
        const res = await authedFetch(ME_URL, { signal: controller.signal });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`/v1/me ${res.status}: ${text || res.statusText}`);
        }
        return transformCurrentUser(await res.json());
    } finally {
        clearTimeout(timer);
    }
}

// Synchronous read of the cached tenant id. Returns null before /v1/me
// has resolved — callers (e.g. the label LS cache) must handle that
// case by skipping persistence rather than blocking render.
export function getCurrentTenantIdSync() {
    return _cache?.currentTenant?.tenantId || null;
}

export function getCurrentUserIdSync() {
    return _cache?.userId || null;
}

export function getCurrentUser() {
    if (_cache) return Promise.resolve(_cache);
    if (_inflight) return _inflight;
    const authContextEpoch = getAuthIdentityEpoch();
    const request = fetchMe()
        .then((u) => {
            if (getAuthIdentityEpoch() !== authContextEpoch) return null;
            _cache = u;
            if (u) _recovery.reset();
            else _recovery.failure();
            emitChange();
            return u;
        })
        .catch((err) => {
            if (getAuthIdentityEpoch() !== authContextEpoch) return null;
            console.warn("[userInfo] /v1/me failed", err);
            if (AUTH_REQUIRED) {
                _recovery.failure();
                return null;
            }
            _cache = LOCAL_DEV_USER;
            return _cache;
        })
        .finally(() => { if (_inflight === request) _inflight = null; });
    _inflight = request;
    return request;
}

export function useCurrentUser() {
    const authContextEpoch = useSyncExternalStore(
        onAuthIdentityChange,
        getAuthIdentityEpoch,
        getAuthIdentityEpoch,
    );
    const user = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => { void getCurrentUser(); }, [authContextEpoch]);
    return user;
}

export function _resetForTests() {
    _recovery.reset();
    _cache = null;
    _inflight = null;
}
